import type { SetUpTOTPOutput } from 'aws-amplify/auth';
import {
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  setUpTOTP,
  signIn,
  signOut as amplifySignOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { create } from 'zustand';

export interface AuthUser {
  userId: string;
  email: string;
  groups: string[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isAdmin: boolean;
  mfaRequired: boolean;
  mfaEnabled: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  confirmMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  getValidToken: () => Promise<string | null>;
  fetchMfaStatus: () => Promise<void>;
  setupTotp: () => Promise<SetUpTOTPOutput>;
  verifyTotp: (code: string) => Promise<void>;
}

/**
 * Mutex to prevent concurrent token refresh requests.
 * Module-level to persist across store operations.
 * This is intentional: if multiple components request tokens simultaneously,
 * we deduplicate to a single Cognito call for efficiency.
 */
let tokenPromise: Promise<string | null> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isAdmin: false,
  mfaRequired: false,
  mfaEnabled: false,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      // Clear any stale Cognito session before attempting sign-in
      try {
        await amplifySignOut();
      } catch (_error) {
        // Ignore - no session to clear
      }

      const { isSignedIn, nextStep } = await signIn({ username: email, password });

      if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        set({ mfaRequired: true, isLoading: false });
        return;
      }

      if (!isSignedIn) {
        throw new Error('Sign in failed');
      }

      // Fetch user info and groups
      await get().checkAuthStatus();

      const { isAuthenticated, isAdmin } = get();
      if (isAuthenticated && !isAdmin) {
        await get().logout();
        throw new Error('Unable to sign in');
      }
      if (!isAuthenticated) {
        throw new Error('Unable to verify account. Please try again.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  confirmMfa: async (code: string) => {
    set({ isLoading: true, error: null });
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: code });

      if (!isSignedIn) {
        throw new Error('MFA verification failed');
      }

      set({ mfaRequired: false });
      await get().checkAuthStatus();

      const { isAuthenticated, isAdmin } = get();
      if (isAuthenticated && !isAdmin) {
        await get().logout();
        throw new Error('Unable to sign in');
      }
      if (!isAuthenticated) {
        throw new Error('Unable to verify account. Please try again.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'MFA verification failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    try {
      await amplifySignOut();
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        error: null,
      });
    }
  },

  checkAuthStatus: async () => {
    set({ isLoading: true });
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken;
      const groups = (idToken?.payload?.['cognito:groups'] as string[] | undefined) ?? [];

      const user: AuthUser = {
        userId: currentUser.userId,
        email: currentUser.signInDetails?.loginId ?? currentUser.username,
        groups,
      };

      const isAdmin = groups.includes('admin');

      set({
        user,
        isAuthenticated: true,
        isAdmin,
        isLoading: false,
        error: null,
      });

      if (isAdmin) {
        await get().fetchMfaStatus();
      }
    } catch (_error) {
      // Intentionally swallow errors here: any failure to get current user
      // (expired session, no session, network error) means user is not authenticated.
      // This is expected behavior on initial app load when no session exists.
      set({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
      });
    }
  },

  getValidToken: async () => {
    // If a token fetch is already in progress, wait for it
    if (tokenPromise) {
      return tokenPromise;
    }

    tokenPromise = (async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: false });
        return session.tokens?.idToken?.toString() ?? null;
      } catch (_error) {
        return null;
      } finally {
        tokenPromise = null;
      }
    })();

    return tokenPromise;
  },

  fetchMfaStatus: async () => {
    try {
      const preference = await fetchMFAPreference();
      const hasTotpEnabled =
        preference.enabled?.includes('TOTP') === true || preference.preferred === 'TOTP';
      set({ mfaEnabled: hasTotpEnabled });
    } catch (_error) {
      set({ mfaEnabled: false });
    }
  },

  setupTotp: () => {
    return setUpTOTP();
  },

  verifyTotp: async (code: string) => {
    await verifyTOTPSetup({ code });
    try {
      await updateMFAPreference({ totp: 'PREFERRED' });
    } catch (_error) {
      // TOTP device is registered but preference update failed.
      // Re-sync status from Cognito to reflect actual state.
      await get().fetchMfaStatus();
      return;
    }
    set({ mfaEnabled: true });
  },
}));
